import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import EN from "@/i18n/en.json";
import HI from "@/i18n/hi.json";
import GU from "@/i18n/gu.json";
import { preloadFontsFor } from "@/lib/fontPreload";

/**
 * Trilingual EN · हिन्दी · ગુજરાતી i18n (Phase A).
 *
 * Backward-compatible with the earlier 2-way EN⇌GU shape:
 *   • `lang` is now `"en" | "hi" | "gu"` (still string).
 *   • `toggle()` cycles EN → HI → GU → EN (older 2-way callers just get
 *     one extra step; no crashes).
 *   • `setLang("gu")` and `setLang("hi")` both work.
 *   • `t(key, fallback)` unchanged — missing keys fall back to English
 *     and never leak raw keys to the UI.
 *
 * Dictionaries live in /src/i18n/{en,hi,gu}.json so they can be handed
 * to a professional translator without touching component code.
 */

export const SUPPORTED_LANGS = ["en", "hi", "gu"];
export const LANG_HTML = { en: "en", hi: "hi", gu: "gu" };

const DICTS = { en: EN, hi: HI, gu: GU };

// Legacy inline dictionary kept as a *secondary* fallback so any key that
// existed in the old bilingual DICT continues to work even if we forgot
// to migrate it to the JSON files yet. Loaded lazily at read-time.
const LEGACY_FALLBACK = {};

function pickLang(candidate) {
  if (typeof candidate !== "string") return "en";
  const norm = candidate.toLowerCase().slice(0, 2);
  return SUPPORTED_LANGS.includes(norm) ? norm : "en";
}

const I18nContext = createContext({
  lang: "en",
  toggle: () => {},
  setLang: () => {},
  t: (k) => k,
});

const STORAGE_KEY = "sf_lang";

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    try {
      return pickLang(window.localStorage.getItem(STORAGE_KEY));
    } catch (_e) {
      return "en";
    }
  });

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, lang); } catch (_e) { /* storage disabled */ }
    if (typeof document !== "undefined") {
      document.documentElement.lang = LANG_HTML[lang] || "en";
      document.documentElement.setAttribute("data-lang", lang);
    }
    // Preload only the script actually needed for above-the-fold text
    // (Devanagari for hi, Gujarati for gu). English visitors never pay
    // for the Indic bytes. Safe to call every time — internal dedupe.
    preloadFontsFor(lang);
  }, [lang]);

  const setLang = useCallback((l) => setLangState(pickLang(l)), []);

  const toggle = useCallback(() => setLangState((prev) => {
    const idx = SUPPORTED_LANGS.indexOf(prev);
    return SUPPORTED_LANGS[(idx + 1) % SUPPORTED_LANGS.length];
  }), []);

  const t = useCallback(
    (key, fallback) => {
      const dict = DICTS[lang] || DICTS.en;
      if (Object.prototype.hasOwnProperty.call(dict, key)) return dict[key];
      // Fallback chain: current lang → English → legacy inline → provided → key
      if (Object.prototype.hasOwnProperty.call(DICTS.en, key)) return DICTS.en[key];
      if (Object.prototype.hasOwnProperty.call(LEGACY_FALLBACK, key)) {
        return LEGACY_FALLBACK[key][lang] || LEGACY_FALLBACK[key].en;
      }
      if (fallback !== undefined) return fallback;
      // Only warn in dev; never leak raw keys to production UI (return English
      // if present, otherwise the fallback the caller provided, otherwise "").
      if (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        if (process.env.NODE_ENV === "development") console.debug("[i18n] missing key:", key);
      }
      return "";
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, toggle, setLang, t }), [lang, toggle, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
